import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import api from '../services/api';
import { UserIcon, EnvelopeIcon, LockClosedIcon, BookOpenIcon } from '@heroicons/react/24/outline';

const SignupPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [studentType, setStudentType] = useState('shs1');
  const [role, setRole] = useState('student');
  const [courses, setCourses] = useState([]);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  // Fetch available courses when component mounts
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await api.get('/courses/public');
        setCourses(response.data);
      } catch (err) {
        console.error('Failed to fetch courses:', err);
      }
    };
    fetchCourses();
  }, []);

  const handleCourseSelection = (courseId) => {
    setSelectedCourses(prev => {
      if (prev.includes(courseId)) {
        return prev.filter(id => id !== courseId);
      } else {
        return [...prev, courseId];
      }
    });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    if (role === 'student' && selectedCourses.length === 0) {
      setError('Please select at least one course');
      setLoading(false);
      return;
    }
    
    try {
      const registrationData = { 
        name, 
        email, 
        password, 
        role, 
        studentType: role === 'student' ? studentType : undefined,
        courseIds: role === 'student' ? selectedCourses : undefined
      };

      await api.post('/auth/register', registrationData);
      await login(email, password);
      // Redirect based on role
      if (role === 'teacher') {
        navigate('/teachers/dashboard');
      } else {
        navigate('/students/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-gray-50">
      {/* Left side - Signup Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="mb-8 text-center">
            <img 
              src="/debest.jpg" 
              alt="DeBest College logo" 
              className="w-24 h-24 mx-auto mb-4 object-contain"
            />
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Create Account</h1>
            <p className="text-gray-600">Join us and start your learning journey</p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Full Name
              </label>
              <div className="relative">
                <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Enter your full name"
                  className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <div className="relative">
                <EnvelopeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <div className="relative">
                <LockClosedIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Create a password"
                  className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  required
                />
              </div>
            </div>

            {/* Role selection */}
            <div>
              <div className="block text-sm font-medium text-gray-700 mb-2">I am a</div>
              <div className="flex items-center space-x-3">
                <label className={`px-3 py-2 rounded-lg cursor-pointer border ${role === 'student' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300'}`}>
                  <input type="radio" name="role" value="student" checked={role === 'student'} onChange={() => setRole('student')} className="hidden" />
                  Student
                </label>
                <label className={`px-3 py-2 rounded-lg cursor-pointer border ${role === 'teacher' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300'}`}>
                  <input type="radio" name="role" value="teacher" checked={role === 'teacher'} onChange={() => setRole('teacher')} className="hidden" />
                  Teacher
                </label>
              </div>
            </div>

            {/* Student-only fields */}
            {role === 'student' && (
              <>
                <div>
                  <label htmlFor="studentType" className="block text-sm font-medium text-gray-700 mb-1">
                    Class Level
                  </label>
                  <select
                    id="studentType"
                    value={studentType}
                    onChange={e => setStudentType(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition appearance-none bg-white"
                    required
                  >
                    <option value="shs1">SHS 1</option>
                    <option value="shs2">SHS 2</option>
                    <option value="shs3">SHS 3</option>
                  </select>
                </div>

                {/* Course Selection */}
                {courses.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center mb-2">
                      <BookOpenIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-sm font-medium text-gray-700">
                        Select your Courses to continue <span className="text-red-500">*</span>
                      </span>
                    </div>
                    <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-lg p-3 space-y-2 bg-gray-50">
                      {courses.map(course => (
                        <label 
                          key={course.id} 
                          className="flex items-start space-x-3 p-2 hover:bg-white rounded cursor-pointer transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={selectedCourses.includes(course.id)}
                            onChange={() => handleCourseSelection(course.id)}
                            className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">
                              {course.title}
                              {course.code && <span className="text-gray-500 ml-1">({course.code})</span>}
                            </p>
                            {course.description && (
                              <p className="text-xs text-gray-600 mt-1">{course.description}</p>
                            )}
                            {course.teacher && (
                              <p className="text-xs text-gray-500 mt-1">Teacher: {course.teacher.name}</p>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                    {selectedCourses.length > 0 && (
                      <p className="text-xs text-blue-600 mt-1">
                        {selectedCourses.length} course{selectedCourses.length !== 1 ? 's' : ''} selected
                      </p>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Courses are selected only when role === 'student' (student-only block above) */}
            
            <button 
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition disabled:opacity-50 disabled:cursor-not-allowed mt-6"
            >
              {loading ? 'Creating account...' : 'Sign Up'}
            </button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-gray-600">
              Already have an account?
            </p>
            <Link 
              to="/login" 
              className="mt-2 inline-block text-blue-600 hover:text-blue-700 font-semibold text-base"
            >
              Sign in →
            </Link>
          </div>
        </div>
      </div>

      {/* Right side - Brand section */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 to-blue-800 items-center justify-center p-12">
        <div className="text-white text-center max-w-lg">
          <h2 className="text-4xl font-bold mb-4">Start Learning Today</h2>
          <p className="text-xl text-blue-100">
            Join thousands of students and teachers in our virtual learning community.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
